import React, { useEffect, useRef, useState } from "react";
import { apiClient } from "@/api/apiClient";
import { GeoData } from "@/types/GeoData";
import { Switch } from "@/components/ui/switch";
import { Card, CardHeader, CardContent } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import GeoMap from "@/components/GeoMap/GeoMap";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Route } from "@/types/routes";
import { Device } from "@/types/devices";

const AdminMapRealtime: React.FC = () => {
    const [types, setTypes] = useState<string[]>([]);
    const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
    const [newPoint, setNewPoint] = useState<GeoData | null>(null);
    const wsRef = useRef<WebSocket | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [routes, setRoutes] = useState<Route[]>([]);
    const [routesMap, setRoutesMap] = useState<Record<string, Route>>({});
    const [devicesMap, setDevicesMap] = useState<Record<string, Device>>({});

    useEffect(() => {
        apiClient.getDeviceTypes().then((data) => {
            const allTypes = data.map((type) => type.id);
            setTypes(allTypes);
            setSelectedTypes(allTypes);
            setLoading(false);
        });
    }, []);

    useEffect(() => {
        if (selectedTypes.length === 0) return;

        setLoading(true);
        apiClient.getLastDevicePoints(selectedTypes)
            .then((points) => {
                points.forEach((point, index) => {
                    setTimeout(() => setNewPoint(point), index * 100);
                });
            })
            .catch((error) => console.error("Ошибка загрузки начальных точек:", error))
            .finally(() => setLoading(false));
    }, [selectedTypes]);

    useEffect(() => {
        if (wsRef.current) wsRef.current.close();
        if (selectedTypes.length === 0) return;

        (async () => {
            const newWs = await apiClient.connectRealtime(
                selectedTypes,
                [],
                (point: GeoData) => setNewPoint(point),
                (error: string) => alert(`Ошибка: ${error}`)
            );
            wsRef.current = newWs;
        })();

        return () => wsRef.current?.close();
    }, [selectedTypes]);

    useEffect(() => {
        apiClient.getActiveRoutes()
            .then((activeRoutes) => {
                const map: Record<string, Route> = {};
                activeRoutes.forEach((route) => {
                    map[route.id] = route;
                });
                setRoutesMap(map);
            })
            .catch((error) => console.error("Ошибка загрузки маршрутов:", error));
    }, []);

    useEffect(() => {
        apiClient.getDevices()
            .then((devices) => {
                const map: Record<string, Device> = {};
                devices.forEach((device) => {
                    map[device.id] = device;
                });
                setDevicesMap(map);
            })
            .catch((error) => console.error("Ошибка загрузки устройств:", error));
    }, []);

    useEffect(() => {
        if (newPoint) {
            const device = devicesMap[newPoint.device];
            if (device && device.route_id) {
                const route = routesMap[device.route_id];
                if (route) {
                    setRoutes((prevRoutes) => {
                        if (prevRoutes.find((r) => r.id === route.id)) return prevRoutes;
                        return [...prevRoutes, route];
                    });
                }
            }
        }
    }, [newPoint, devicesMap, routesMap, routes]);

    const toggleType = (type: string) => {
        if (selectedTypes.length === 1 && selectedTypes.includes(type)) return;
        setSelectedTypes((prev) =>
            prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]
        );
    };

    return (
        <div className="p-6 space-y-4">
            <h1 className="text-xl font-bold">Админ Карта: Реальное Время</h1>

            {loading ? (
                <div className="flex justify-center items-center">
                    <Loader2 className="animate-spin w-8 h-8 text-primary" />
                </div>
            ) : (
                <Card>
                    <CardHeader className="font-semibold">Фильтр типов устройств</CardHeader>
                    <CardContent className="flex flex-wrap gap-4">
                        {types.map((type) => (
                            <div key={type} className="flex items-center space-x-2">
                                <Switch
                                    checked={selectedTypes.includes(type)}
                                    disabled={selectedTypes.length === 1 && selectedTypes.includes(type)}
                                    onCheckedChange={() => toggleType(type)}
                                />
                                <span className="text-sm font-medium">{type}</span>
                            </div>
                        ))}
                    </CardContent>
                </Card>
            )}

            <Card className="max-h-[400px] overflow-hidden">
                <TooltipProvider>
                    <GeoMap deviceTypes={selectedTypes} newPoint={newPoint} routes={routes} />
                </TooltipProvider>
            </Card>
        </div>
    );
};

export default AdminMapRealtime;
